import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Edit, ChevronsUpDown, Download, Palette } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const getNomeCompleto = (usuario) => {
    if (!usuario) return '';
    const nome = usuario.name || usuario.nome || '';
    const sobrenome = usuario.lastname || usuario.sobrenome || '';
    return `${nome} ${sobrenome}`.trim();
};


const ModalAjusteEscala = ({ slot, onClose, onSave, membrosDisponiveis }) => {
    const [novoUsuarioId, setNovoUsuarioId] = useState(slot?.usuario_id || '');
    if (!slot) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(slot.id, novoUsuarioId);
    };

    const handleRemover = () => {
        if (window.confirm('Tem certeza que deseja remover este membro da escala?')) {
            onSave(slot.id, null); // null indica remoção
        }
    };

    const { dataFormatada } = formatarDataCelebracao(slot.data_celebracao);
    const nomeCompletoAtual = getNomeCompleto({ nome: slot.usuario_nome, sobrenome: slot.usuario_sobrenome });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-lg animate-fade-in-scale">
                <h2 className="text-2xl font-bold mb-4 text-slate-800">Alterar Membro da Escala</h2>
                <p className="mb-6 text-slate-600">
                    A trocar <strong className="text-indigo-700">{nomeCompletoAtual}</strong> da celebração de <strong className="text-indigo-700">{dataFormatada}</strong>.
                </p>
                <form onSubmit={handleSubmit}>
                    <label htmlFor="membro-select" className="block text-sm font-medium text-slate-700">Selecione um substituto disponível:</label>
                    <select
                        id="membro-select"
                        value={novoUsuarioId}
                        onChange={(e) => setNovoUsuarioId(e.target.value)}
                        className="mt-1 block w-full p-3 border-slate-300 rounded-md shadow-sm"
                    >
                        <option key={slot.usuario_id} value={slot.usuario_id}>{nomeCompletoAtual} (Manter)</option>
                        {membrosDisponiveis
                            .filter(membro => membro.id !== slot.usuario_id)
                            .map(membro => (<option key={membro.id} value={membro.id}>{getNomeCompleto(membro)}</option>))
                        }
                        {membrosDisponiveis.filter(m => m.id !== slot.usuario_id).length === 0 && <option disabled>Nenhum outro membro disponível</option>}
                    </select>
                    <div className="flex justify-between gap-4 pt-6">
                        <div className="flex gap-2">
                            <button type="button" onClick={onClose} className="px-5 py-2 bg-slate-200 rounded-lg font-semibold text-slate-800 hover:bg-slate-300">Cancelar</button>
                            <button type="button" onClick={handleRemover} className="px-5 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700">Remover</button>
                        </div>
                        <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Confirmar Troca</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ModalTrocarResponsavel = ({ celebracao, onClose, onSave }) => {
    const [novoResponsavelId, setNovoResponsavelId] = useState('');
    if (!celebracao) return null;

    const { dataFormatada } = formatarDataCelebracao(celebracao.data_original);
    const candidatos = celebracao.acolitos.filter(acolito => acolito.usuario_id !== celebracao.responsavel_id);
    const semCandidatos = candidatos.length === 0;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (semCandidatos || !novoResponsavelId) return;
        onSave(celebracao.id, novoResponsavelId);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-lg animate-fade-in-scale">
                <h2 className="text-2xl font-bold mb-4 text-slate-800">Alterar Responsável</h2>
                <p className="mb-6 text-slate-600">
                    O responsável atual é <strong className="text-indigo-700">{celebracao.responsavel}</strong>. Selecione um novo para a celebração de <strong className="text-indigo-700">{dataFormatada}</strong>.
                </p>
                <form onSubmit={handleSubmit}>
                    <label htmlFor="responsavel-select" className="block text-sm font-medium text-slate-700">Selecione um novo responsável:</label>
                    <select
                        id="responsavel-select"
                        value={novoResponsavelId}
                        onChange={(e) => setNovoResponsavelId(e.target.value)}
                        className="mt-1 block w-full p-3 border-slate-300 rounded-md shadow-sm"
                        disabled={semCandidatos}
                    >
                        <option value="" disabled>
                            {semCandidatos ? 'Nenhum outro membro disponível' : 'Selecione um membro...'}
                        </option>
                        {!semCandidatos && candidatos.map(membro => (
                            <option key={membro.usuario_id} value={membro.usuario_id}>{membro.nome_completo}</option>
                        ))}
                    </select>
                    {semCandidatos && (
                        <div className="mt-2 text-red-600 text-sm">Nenhum outro membro disponível para ser responsável nesta celebração.</div>
                    )}
                    <div className="flex justify-end gap-4 pt-6">
                        <button type="button" onClick={onClose} className="px-5 py-2 bg-slate-200 rounded-lg font-semibold text-slate-800 hover:bg-slate-300">Cancelar</button>
                        <button type="submit" disabled={semCandidatos || !novoResponsavelId} className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-300">Confirmar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CORES_LITURGICAS = [
    { nome: 'Branco', valor: '#FFFFFF' },
    { nome: 'Verde', valor: '#008000' },
    { nome: 'Vermelho', valor: '#FF0000' },
    { nome: 'Roxo', valor: '#800080' },
    { nome: 'Preto', valor: '#000000' },
    { nome: 'Roseo', valor: '#FFC0CB' }
];

const ModalAjusteCor = ({ celebracao, onClose, onSave }) => {
    // Permitir múltiplas cores
    const [novaCor, setNovaCor] = useState(() => {
        if (!celebracao?.cor_ombreira) return [];
        if (Array.isArray(celebracao.cor_ombreira)) return celebracao.cor_ombreira;
        if (typeof celebracao.cor_ombreira === 'string' && celebracao.cor_ombreira.includes(',')) {
            return celebracao.cor_ombreira.split(',').map(c => c.trim());
        }
        return [celebracao.cor_ombreira];
    });
    if (!celebracao) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        // Salvar como string separada por vírgula
        onSave(celebracao, novaCor.join(','));
    };

    const { dataFormatada } = formatarDataCelebracao(celebracao.data_original);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-lg animate-fade-in-scale">
                <h2 className="text-2xl font-bold mb-4 text-slate-800">Alterar Cor da Ombreira</h2>
                <p className="mb-6 text-slate-600">
                    Alterando a cor para a celebração de <strong className="text-indigo-700">{dataFormatada} às {celebracao.horario_formatado}</strong>.
                </p>
                <form onSubmit={handleSubmit}>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Selecione até 2 cores litúrgicas:</label>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        {CORES_LITURGICAS.map(cor => (
                            <label key={cor.valor} className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    value={cor.valor}
                                    checked={novaCor.includes(cor.valor)}
                                    disabled={novaCor.length === 2 && !novaCor.includes(cor.valor)}
                                    onChange={e => {
                                        if (e.target.checked) {
                                            if (novaCor.length < 2) setNovaCor([...novaCor, cor.valor]);
                                        } else {
                                            setNovaCor(novaCor.filter(c => c !== cor.valor));
                                        }
                                    }}
                                />
                                <span className="w-5 h-5 rounded-full border border-slate-300" style={{ backgroundColor: cor.valor }}></span>
                                <span>{cor.nome}</span>
                            </label>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        {novaCor.map(cor => (
                            <span key={cor} className="w-8 h-8 rounded-full border border-slate-300" style={{ backgroundColor: cor }} title={cor}></span>
                        ))}
                        <span className="font-mono">{novaCor.join(', ')}</span>
                    </div>
                    <div className="flex justify-end gap-4 pt-6">
                        <button type="button" onClick={onClose} className="px-5 py-2 bg-slate-200 rounded-lg font-semibold text-slate-800 hover:bg-slate-300">Cancelar</button>
                        <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Salvar Cor</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- MODAL PARA EXPORTAR ESCALA POR PERÍODO OU MÊS ---
const ModalExportarPeriodo = ({ open, onClose, onExport }) => {
    const [tipo, setTipo] = useState('mes');
    const [mesReferencia, setMesReferencia] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [formError, setFormError] = useState('');

    useEffect(() => {
        if (!open) {
            setTipo('mes');
            setMesReferencia('');
            setDataInicio('');
            setDataFim('');
        }
    }, [open]);

    const handleExportar = (e) => {
        e.preventDefault();
        setFormError('');
        if (tipo === 'mes' && !mesReferencia) {
            setFormError('Selecione o mês de referência.');
            return;
        }
        if (tipo === 'periodo') {
            if (!dataInicio || !dataFim) {
                setFormError('Preencha as datas de início e fim.');
                return;
            }
            if (dataFim < dataInicio) {
                setFormError('A data de fim não pode ser menor que a de início.');
                return;
            }
        }
        onExport({
            mesReferencia: tipo === 'mes' ? mesReferencia : undefined,
            dataInicio: tipo === 'periodo' ? dataInicio : undefined,
            dataFim: tipo === 'periodo' ? dataFim : undefined
        });
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" aria-modal="true" role="dialog">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md animate-fade-in-scale">
                <h2 className="text-xl font-bold mb-4 text-slate-800">Exportar Escala</h2>
                <form onSubmit={handleExportar} aria-label="Formulário de exportação de escala">
                    <div className="mb-4">
                        <label className="block mb-2 font-medium" htmlFor="tipo-exportacao">Tipo de Exportação:</label>
                        <select id="tipo-exportacao" value={tipo} onChange={e => setTipo(e.target.value)} className="w-full p-2 border rounded" aria-label="Tipo de exportação">
                            <option value="mes">Por mês</option>
                            <option value="periodo">Por período</option>
                        </select>
                    </div>
                    {tipo === 'mes' && (
                        <div className="mb-4">
                            <label className="block mb-2 font-medium" htmlFor="mes-referencia">Mês de referência:</label>
                            <input type="month" id="mes-referencia" value={mesReferencia} onChange={e => setMesReferencia(e.target.value)} className="w-full p-2 border rounded" required aria-label="Mês de referência" />
                        </div>
                    )}
                    {tipo === 'periodo' && (
                        <div className="mb-4 flex gap-2">
                            <div>
                                <label className="block mb-2 font-medium" htmlFor="data-inicio">Data início:</label>
                                <input type="date" id="data-inicio" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="p-2 border rounded" required aria-label="Data início" />
                            </div>
                            <div>
                                <label className="block mb-2 font-medium" htmlFor="data-fim">Data fim:</label>
                                <input type="date" id="data-fim" value={dataFim} onChange={e => setDataFim(e.target.value)} className="p-2 border rounded" required aria-label="Data fim" />
                            </div>
                        </div>
                    )}
                    {formError && <div className="text-red-500 mb-2" aria-live="assertive">{formError}</div>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-200 rounded-lg font-semibold text-slate-800 hover:bg-slate-300" aria-label="Cancelar">Cancelar</button>
                        <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700" aria-label="Exportar">Exportar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- FUNÇÃO AUXILIAR PARA FORMATAR A DATA ---
const formatarDataCelebracao = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return { dataFormatada: 'Data Inválida', diaDaSemana: 'Dia Inválido' };
    let date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        date = new Date(dateString + 'T00:00:00Z');
    } else {
        date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return { dataFormatada: 'Data Inválida', diaDaSemana: 'Dia Inválido' };
    const diasDaSemana = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
    const dia = String(date.getUTCDate()).padStart(2, '0');
    const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
    const ano = date.getUTCFullYear();
    return { dataFormatada: `${dia}/${mes}/${ano}`, diaDaSemana: diasDaSemana[date.getUTCDay()] };
};
function GerarEscala({ grupo: grupoProp, usuarioPainel: usuarioPainelProp, token: tokenProp }) {
    const [periodos, setPeriodos] = useState([]);
    const [periodoSelecionadoId, setPeriodoSelecionadoId] = useState('');
    const [escalaRascunho, setEscalaRascunho] = useState([]);
    const [membrosParaTroca, setMembrosParaTroca] = useState([]);
    const [activeTab, setActiveTab] = useState('gerar');
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mensagem, setMensagem] = useState('');
    const [erro, setErro] = useState('');
    const [editingSlot, setEditingSlot] = useState(null);
    const [expandedRowId, setExpandedRowId] = useState(null);

    const grupo = grupoProp;
    const [editingResponsavelSlot, setEditingResponsavelSlot] = useState(null);
    const [editingColorSlot, setEditingColorSlot] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [rascunhoDisponivel, setRascunhoDisponivel] = useState(false);
    // Definir grupoDoUsuario para uso nos renders
    const grupoDoUsuario = grupo?.tipo;

    // Nova função para verificar se existe rascunho
    const verificarRascunhoExistente = useCallback(async (periodo) => {
        try {
            const mesReferencia = periodo?.data_inicio?.slice(0, 7) || periodo?.mesReferencia?.slice(0, 7) || periodo?.mes_referencia?.slice(0, 7);
            if (!mesReferencia) return;

            const grupoId = grupo?.grupo_id;
            const res = await api.get('/admin/escalas/rascunho', { params: { mesReferencia, grupoId, _t: Date.now() } });
            const rascunho = res.data || [];

            if (rascunho.length > 0) {
                setEscalaRascunho(rascunho);
                setRascunhoDisponivel(true);
                setActiveTab('revisar');
                setMensagem('Rascunho encontrado! Você pode revisar e publicar a escala.');
            } else {
                setRascunhoDisponivel(false);
            }
        } catch (err) {
            setRascunhoDisponivel(false);
        }
    }, [grupo]);

    const carregarDadosIniciais = useCallback(async () => {
    setLoading(true); setErro('');
    try {
        const grupoId = grupo?.grupo_id;
        if (!grupoId) {
            setErro("Dados de utilizador ou grupo incompletos.");
            setLoading(false);
            return;
        }
        const periodosRes = await api.get(`/admin/periodos/grupo/${grupoId}`);
        const periodosData = periodosRes.data || [];
        setPeriodos(periodosData);
        if (periodosData.length > 0) {
            setPeriodoSelecionadoId(periodosData[0].id);
            await verificarRascunhoExistente(periodosData[0]);
        }
    } catch (err) {
        setErro("Falha ao carregar os dados iniciais.");
    }
    finally { setLoading(false); }
}, [verificarRascunhoExistente, grupo?.grupo_id]);

    const getMesReferencia = useCallback(() => {
        if (!periodoSelecionadoId) return null;
        const periodoEscolhido = periodos.find(p => p.id === parseInt(periodoSelecionadoId));
        return periodoEscolhido?.data_inicio?.slice(0, 7) || periodoEscolhido?.mesReferencia?.slice(0, 7) || periodoEscolhido?.mes_referencia?.slice(0, 7);
    }, [periodos, periodoSelecionadoId]);

    const fetchRascunho = useCallback(async () => {
        const mesReferencia = getMesReferencia();
        if (!mesReferencia) { 
            setErro("Não foi possível determinar o mês de referência."); 
            return;
        }
        setIsSubmitting(true);
        setErro(''); // Limpar erros anteriores
        try {
            const grupoId = grupo?.grupo_id;
            const res = await api.get('/admin/escalas/rascunho', { params: { mesReferencia, grupoId, _t: Date.now() } });
            const rascunho = res.data || [];
            if (rascunho.length > 0) {
                setEscalaRascunho(rascunho);
                setRascunhoDisponivel(true);
                setActiveTab('revisar');
                if (!mensagem) { // Só mostrar se não há outra mensagem
                    setMensagem('Rascunho carregado com sucesso!');
                }
            } else {
                setRascunhoDisponivel(false);
                setMensagem('Nenhum rascunho encontrado para este período. Gere um novo rascunho.');
                setActiveTab('gerar');
            }
        } catch (err) { 
            setErro("Não foi possível carregar o rascunho da escala."); 
            setRascunhoDisponivel(false);
        }
        finally { setIsSubmitting(false); }
    }, [getMesReferencia, mensagem, grupo]);

    // Função para verificar rascunho quando o período muda
    const verificarRascunhoAoMudarPeriodo = useCallback(async (novoPeriodoId) => {
        if (!novoPeriodoId) return;
        
        const periodoEscolhido = periodos.find(p => p.id === parseInt(novoPeriodoId));
        if (periodoEscolhido) {
            await verificarRascunhoExistente(periodoEscolhido);
        }
    }, [periodos, verificarRascunhoExistente]);

    useEffect(() => { carregarDadosIniciais() }, [carregarDadosIniciais]);

    // Efeito para verificar rascunho quando o período selecionado mudar
    useEffect(() => {
        if (periodoSelecionadoId && periodos.length > 0) {
            verificarRascunhoAoMudarPeriodo(periodoSelecionadoId);
        }
    }, [periodoSelecionadoId, periodos, verificarRascunhoAoMudarPeriodo]);

    const handleOpenModalAjuste = async (celebracao, membroAtual) => {
        setIsSubmitting(true);
        try {
            const response = await api.get('/admin/escalas/membros-disponiveis', { params: { data: celebracao.data_original, horario_id: celebracao.horario_id } });
            setMembrosParaTroca(response.data || []);
            setEditingSlot({
                id: membroAtual.id_escala,
                usuario_id: membroAtual.usuario_id,
                usuario_nome: membroAtual.nome,
                usuario_sobrenome: membroAtual.sobrenome,
                data_celebracao: celebracao.data_original,
                hora: celebracao.horario_original
            });
        } catch (err) { setErro("Falha ao buscar membros disponíveis."); }
        finally { setIsSubmitting(false); }
    };

    const handleOpenModalResponsavel = (celebracao) => setEditingResponsavelSlot(celebracao);
    const handleOpenModalCor = (celebracao) => setEditingColorSlot(celebracao);

    const handleGerarEscala = async (e) => {
        e.preventDefault();
        const mesReferencia = getMesReferencia();
        const grupoId = grupo?.grupo_id;
        if (!mesReferencia || !grupoId) { setErro("Período ou grupo inválido."); return; }
        setIsSubmitting(true); setMensagem(''); setErro('');
        try {
            const response = await api.post('/admin/escalas/gerar', { mesReferencia, grupoId });
            setMensagem(response.data.message || 'Rascunho gerado!');
            await fetchRascunho();
        } catch (err) {
            let errorMessage = 'Ocorreu um erro desconhecido ao gerar a escala.';
            if (err.response?.data?.error) {
                const errorData = err.response.data.error;
                if (typeof errorData === 'object' && errorData.message) {
                    errorMessage = errorData.message;
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            setErro(errorMessage);
        }
        finally { setIsSubmitting(false); }
    };

    const handleAjustarEscala = async (escalaId, novoUsuarioId) => {
        try {
            if (novoUsuarioId === null) {
                // Remover membro
                await api.delete(`/admin/escalas/remover-membro/${escalaId}`);
                
                // Atualização otimista: remover membro do estado local
                setEscalaRascunho(prev => prev.map(celebracao => {
                    const novosAcolitos = (celebracao.acolitos || []).filter(a => a.id_escala !== escalaId);
                    return { ...celebracao, acolitos: novosAcolitos };
                }));
                
                toast.success('Membro removido com sucesso!');
            } else {
                // Substituir membro
                await api.put(`/admin/escalas/ajustar/${escalaId}`, { novo_usuario_id: novoUsuarioId });
                
                // Atualização otimista: substituir membro no estado local
                const novoMembro = membrosParaTroca.find(m => String(m.id) === String(novoUsuarioId));
                if (novoMembro) {
                    const nomeCompleto = getNomeCompleto(novoMembro);
                    setEscalaRascunho(prev => prev.map(celebracao => {
                        const novosAcolitos = (celebracao.acolitos || []).map(a => {
                            if (a.id_escala === escalaId) {
                                return {
                                    ...a,
                                    usuario_id: novoMembro.id,
                                    nome: novoMembro.name || novoMembro.nome || '',
                                    sobrenome: novoMembro.lastname || novoMembro.sobrenome || '',
                                    nome_completo: nomeCompleto
                                };
                            }
                            return a;
                        });
                        return { ...celebracao, acolitos: novosAcolitos };
                    }));
                }
                
                toast.success('Troca realizada com sucesso!');
            }
            setEditingSlot(null);
            await fetchRascunho();
        } catch (err) {
            toast.error('Falha ao realizar a troca ou remoção.');
        }
    };

    const handleSalvarNovoResponsavel = async (celebracaoId, novoResponsavelId) => {
        try {
            const celebracao = escalaRascunho.find(c => c.id === celebracaoId);
            if (!celebracao) { setErro('Erro ao encontrar os dados da celebração.'); return; }
            await api.put(`/admin/escalas/definir-responsavel`, { data_celebracao: celebracao.data_original, horario_id: celebracao.horario_id, novo_responsavel_id: novoResponsavelId });
            
            // Atualização otimista: definir o novo responsável no estado local
            const novoResponsavelMembro = celebracao.acolitos.find(a => String(a.usuario_id) === String(novoResponsavelId));
            const novoResponsavelNome = novoResponsavelMembro ? novoResponsavelMembro.nome_completo : 'Responsável';
            setEscalaRascunho(prev => prev.map(c => {
                if (c.id === celebracaoId) {
                    return {
                        ...c,
                        responsavel_id: novoResponsavelId,
                        responsavel: novoResponsavelNome
                    };
                }
                return c;
            }));
            
            setEditingResponsavelSlot(null);
            await fetchRascunho();
            toast.success('Responsável alterado com sucesso!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao salvar responsável.');
        }
    };

    const handleSalvarCor = async (celebracao, novaCor) => {
        try {
            const grupo_id = grupo?.grupo_id;
            const dadosParaEnviar = {
                data_celebracao: celebracao.data_celebracao || celebracao.data_original,
                horario_id: celebracao.horario_id,
                cor_ombreira: novaCor,
                grupo_id
            };
            await api.put('/admin/escalas/definir-cor', dadosParaEnviar, {
                headers: tokenProp ? { Authorization: `Bearer ${tokenProp}` } : {}
            });
            
            // Atualização otimista: definir a nova cor de ombreira no estado local
            setEscalaRascunho(prev => prev.map(c => {
                if (c.id === celebracao.id) {
                    return {
                        ...c,
                        cor_ombreira: novaCor
                    };
                }
                return c;
            }));
            
            setEditingColorSlot(null);
            await fetchRascunho();
            toast.success('Cor alterada com sucesso!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao salvar a nova cor.');
        }
    };

    const handlePublicar = async () => {
        const mesReferencia = getMesReferencia();
        const grupoId = grupo?.grupo_id;
        if (!mesReferencia || !grupoId) { setErro("Nenhum período ou grupo para publicar."); return; }
        if (window.confirm(`Tem a certeza que deseja publicar a escala para ${mesReferencia}?`)) {
            setIsSubmitting(true);
            try {
                const response = await api.post('/admin/escalas/publicar', { mesReferencia, grupoId });
                setMensagem(response.data.message || "Escala publicada com sucesso!");
                setEscalaRascunho([]);
                setRascunhoDisponivel(false);
                setActiveTab('gerar');
                toast.success('Escala publicada com sucesso!');
            } catch (err) { 
                toast.error(err.response?.data?.error || "Falha ao publicar a escala.");
                setErro(err.response?.data?.error || "Falha ao publicar a escala."); 
            }
            finally { setIsSubmitting(false); }
        }
    };


    const handleExportarComPeriodo = async ({ mesReferencia, dataInicio, dataFim }) => {
        setIsSubmitting(true);
        setShowExportModal(false);
        setErro('');
        try {
            const grupoId = grupo?.grupo_id;
            const params = { grupoId };
            if (mesReferencia) params.mesReferencia = mesReferencia;
            if (dataInicio && dataFim) {
                params.dataInicio = dataInicio;
                params.dataFim = dataFim;
            }
            const response = await api.get('/admin/escalas/exportar', { params, responseType: 'blob' });
            const periodoLabel = mesReferencia || `${dataInicio}_a_${dataFim}`;
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `escala_exportada_${periodoLabel}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Exportação realizada com sucesso!');
        } catch (err) {
            toast.error("Ocorreu um erro ao exportar a escala.");
            setErro("Ocorreu um erro ao exportar a escala.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderGerarTab = () => (
        <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-xl font-bold text-slate-700 mb-6">1. Gerar Rascunho da Escala</h2>
            

            
            {periodos.length > 0 ? (
                <form onSubmit={handleGerarEscala}>
                    <label htmlFor="periodo-select" className="block text-sm font-medium text-slate-700">Selecione o Período</label>
                    <select 
                        id="periodo-select" 
                        value={periodoSelecionadoId} 
                        onChange={(e) => setPeriodoSelecionadoId(e.target.value)} 
                        className="mt-1 block w-full p-3 border-slate-300 rounded-md shadow-sm"
                    >
                        {periodos.map(p => <option key={p.id} value={p.id}>{p.mes_referencia || p.mesReferencia}</option>)}
                    </select>
                    
                    {rascunhoDisponivel && (
                        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                            ⚠️ Gerar um novo rascunho irá substituir o rascunho existente.
                        </div>
                    )}
                    
                    <button 
                        type="submit" 
                        disabled={isSubmitting || !periodoSelecionadoId} 
                        className="mt-6 w-full bg-indigo-600 text-white font-semibold py-3 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors"
                    >
                        {isSubmitting ? 'A Gerar...' : (rascunhoDisponivel ? 'Gerar Novo Rascunho' : 'Gerar Escala')}
                    </button>
                </form>
            ) : (<div className="text-center text-slate-500 bg-slate-100 p-4 rounded-lg"><p>Nenhum período de escala encontrado.</p></div>)}
        </div>
    );

    const renderRevisarTab = () => {
        // Se não há rascunho disponível, mostrar mensagem
        if (!rascunhoDisponivel || escalaRascunho.length === 0) {
            return (
                <div className="text-center py-12">
                    <div className="bg-slate-100 p-8 rounded-xl max-w-md mx-auto">
                        <h3 className="text-lg font-semibold text-slate-700 mb-4">Nenhum Rascunho Disponível</h3>
                        <p className="text-slate-600 mb-6">
                            Não há rascunho de escala disponível para o período selecionado. 
                            Gere um novo rascunho na aba "Gerar Rascunho".
                        </p>
                        <button
                            onClick={() => setActiveTab('gerar')}
                            className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700"
                        >
                            Ir para Gerar Rascunho
                        </button>
                    </div>
                </div>
            );
        }

        const tableHeaders = grupoDoUsuario === 'Coroinhas'
            ? ['Data', 'Dia', 'Horário', 'Membros', 'Ombreira', 'Ações']
            : ['Data', 'Dia', 'Horário', 'Membros', 'Responsável', 'Ações'];

        return (
            <div>
                <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4 gap-4">
                    <h2 className="text-xl font-bold text-slate-700">2. Rever e Publicar Escala</h2>
                    <div className="flex gap-2">
                        {escalaRascunho.length > 0 && (
                            <button
                                onClick={() => setShowExportModal(true)}
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg shadow-md hover:bg-teal-700 flex items-center gap-2 disabled:bg-gray-400"
                            >
                                <Download size={16} /> Exportar
                            </button>
                        )}
                        {escalaRascunho.length > 0 && (<button onClick={handlePublicar} className="px-6 py-2 bg-green-600 text-white font-bold rounded-lg shadow-md hover:bg-green-700">Publicar</button>)}
                    </div>
                </div>

                <div className="hidden md:block bg-white shadow-md rounded-lg overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-slate-50">
                            <tr>
                                {tableHeaders.map(header => (
                                    <th key={header} className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {escalaRascunho.map(celebracao => {
                                const { dataFormatada, diaDaSemana } = formatarDataCelebracao(celebracao.data_original);


                                return (
                                    <React.Fragment key={celebracao.id}>
                                        <tr className="hover:bg-slate-50">
                                            <td className="px-6 py-4 whitespace-nowrap">{dataFormatada}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">{diaDaSemana}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">{celebracao.horario_formatado}</td>
                                            <td className="px-6 py-4">{celebracao.acolitos.map(a => (<div key={a.id_escala}>{a.nome_completo}</div>))}</td>
                                            {grupoDoUsuario === 'Acolitos' && (
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span>{celebracao.responsavel}</span>
                                                        <button onClick={() => handleOpenModalResponsavel(celebracao)} className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"><Edit size={14} /></button>
                                                    </div>
                                                </td>
                                            )}
                                            {grupoDoUsuario === 'Coroinhas' && (
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        {(celebracao.cor_ombreira || '').split(',').map((cor, idx) => (
                                                            <span key={cor+idx} className="w-5 h-5 rounded-full border border-slate-300" style={{ backgroundColor: cor.trim() }}></span>
                                                        ))}
                                                        <span>{celebracao.cor_ombreira}</span>
                                                        <button onClick={() => handleOpenModalCor(celebracao)} className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"><Palette size={14} /></button>
                                                    </div>
                                                </td>
                                            )}

                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <button onClick={() => setExpandedRowId(expandedRowId === celebracao.id ? null : celebracao.id)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full"><ChevronsUpDown size={18} /></button>
                                            </td>
                                        </tr>
                                        {expandedRowId === celebracao.id && (
                                            <tr className="bg-slate-100"><td colSpan={tableHeaders.length} className="p-4">
                                                <h4 className="font-bold mb-2 text-sm text-slate-600">Editar Membros Individualmente:</h4>
                                                <ul className="divide-y divide-slate-200">{celebracao.acolitos.map(acolito => (<li key={acolito.id_escala} className="flex justify-between items-center py-2"><span>{acolito.nome_completo}</span><button onClick={() => handleOpenModalAjuste(celebracao, acolito)} className="px-3 py-1 bg-white border border-slate-300 rounded-md text-xs text-slate-700 hover:bg-slate-50">Alterar</button></li>))}</ul>
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="md:hidden space-y-4">
                    {escalaRascunho.map(celebracao => {
                        const { dataFormatada, diaDaSemana } = formatarDataCelebracao(celebracao.data_original);

                        // Mostra o valor da cor_ombreira para cada celebração (mobile)

                        return (
                            <div key={celebracao.id} className="bg-white p-4 rounded-lg shadow-md">
                                <div className="font-bold text-slate-800">{dataFormatada} - {diaDaSemana} - {celebracao.horario_formatado}</div>
                                <div className="mt-2 text-sm"><strong>Membros:</strong> {celebracao.acolitos.map(a => a.nome_completo).join(', ')}</div>

                                {grupoDoUsuario === 'Acolitos' && (
                                    <div className="mt-1 text-sm flex justify-between items-center">
                                        <span><strong>Responsável:</strong> {celebracao.responsavel}</span>
                                        <button onClick={() => handleOpenModalResponsavel(celebracao)} className="text-blue-600 text-xs font-semibold flex items-center gap-1"><Edit size={12} /> ALTERAR</button>
                                    </div>
                                )}
                                {grupoDoUsuario === 'Coroinhas' && (
                                    <div className="mt-1 text-sm flex justify-between items-center">
                                        <div className='flex items-center gap-2'>
                                            <strong>Ombreira:</strong>
                                            {/* Mostra o valor da cor da ombreira (mobile) */}
                                            <span className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: celebracao.cor_ombreira || '#ccc' }}></span>
                                            <span>{celebracao.cor_ombreira}</span>
                                        </div>
                                        <button onClick={() => handleOpenModalCor(celebracao)} className="text-blue-600 text-xs font-semibold flex items-center gap-1"><Palette size={12} /> ALTERAR</button>
                                    </div>
                                )}

                                <div className="mt-2 pt-2 border-t">{celebracao.acolitos.map(acolito => (<div key={acolito.id_escala} className="flex justify-between items-center py-1"><span className="text-sm">{acolito.nome_completo}</span><button onClick={() => handleOpenModalAjuste(celebracao, acolito)} className="text-indigo-600 text-xs font-semibold">ALTERAR</button></div>))}</div>
                            </div>
                        )
                    })}
                </div>
            </div>
        );
    }

    if (loading) return <p className="text-center p-8">A carregar...</p>;

    return (
        <div className="p-4 bg-slate-100 rounded-lg shadow-inner min-h-[500px]">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <nav className="border-b border-slate-200 mb-6 -mb-px flex space-x-6 overflow-x-auto">
                <button 
                    onClick={() => setActiveTab('gerar')} 
                    className={`flex-shrink-0 py-3 px-1 border-b-2 font-medium ${activeTab === 'gerar' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:border-slate-300'}`}
                >
                    1. Gerar Rascunho
                </button>
                <button 
                    onClick={fetchRascunho} 
                    disabled={!periodoSelecionadoId || isSubmitting} 
                    className={`flex-shrink-0 py-3 px-1 border-b-2 font-medium disabled:text-slate-300 ${activeTab === 'revisar' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:border-slate-300'} relative pr-6`}
                >
                    2. Rever & Publicar
                    {rascunhoDisponivel && (
                        <span className="absolute top-2 right-1 w-3 h-3 bg-green-500 rounded-full"></span>
                    )}
                </button>
            </nav>
            {erro && <div className="mb-4 p-3 text-center bg-red-100 text-red-800 rounded-lg cursor-pointer" onClick={() => setErro('')}>{erro}</div>}
            {mensagem && <div className="mb-4 p-3 text-center bg-green-100 text-green-800 rounded-lg cursor-pointer" onClick={() => setMensagem('')}>{mensagem}</div>}

            {activeTab === 'gerar' && renderGerarTab()}
            {activeTab === 'revisar' && renderRevisarTab()}

            <ModalExportarPeriodo
                open={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={handleExportarComPeriodo}
            />

            {editingSlot && <ModalAjusteEscala slot={editingSlot} onClose={() => setEditingSlot(null)} onSave={handleAjustarEscala} membrosDisponiveis={membrosParaTroca} />}
            {grupoDoUsuario === 'Acolitos' && editingResponsavelSlot && <ModalTrocarResponsavel celebracao={editingResponsavelSlot} onClose={() => setEditingResponsavelSlot(null)} onSave={handleSalvarNovoResponsavel} />}
            {grupoDoUsuario === 'Coroinhas' && editingColorSlot && <ModalAjusteCor celebracao={editingColorSlot} onClose={() => setEditingColorSlot(null)} onSave={handleSalvarCor} />}
        </div>
    );
}
export default GerarEscala;